import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import StudentLayout from '@/components/layouts/StudentLayout';
import { 
  Zap, ArrowLeft, Trophy, Clock, Star, 
  RotateCcw, CheckCircle, XCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const QuickQuizGame = () => {
  const navigate = useNavigate();
  const { user, api } = useAuth();
  const [searchParams] = useSearchParams();
  const subject = searchParams.get('subject') || 'maths';
  
  const [gameState, setGameState] = useState('ready'); // ready, playing, finished
  const [timeLeft, setTimeLeft] = useState(60);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [questionsAnswered, setQuestionsAnswered] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const timerRef = useRef(null);

  // Question banks by subject
  const questionBanks = {
    maths: [
      { q: "7 × 8 = ?", options: ["54", "56", "63", "64"], correct: 1 },
      { q: "What is 25% of 80?", options: ["15", "20", "25", "30"], correct: 1 },
      { q: "12 + 19 = ?", options: ["29", "30", "31", "32"], correct: 2 },
      { q: "What is 144 ÷ 12?", options: ["11", "12", "13", "14"], correct: 1 },
      { q: "9² = ?", options: ["72", "81", "90", "99"], correct: 1 },
      { q: "What is 3/4 + 1/4?", options: ["1/2", "3/4", "1", "4/4"], correct: 2 },
      { q: "15 × 6 = ?", options: ["80", "85", "90", "95"], correct: 2 },
      { q: "What is 50% of 120?", options: ["50", "55", "60", "65"], correct: 2 },
      { q: "100 - 37 = ?", options: ["63", "67", "73", "77"], correct: 0 },
      { q: "6 × 9 = ?", options: ["45", "52", "54", "56"], correct: 2 },
      { q: "What is 1/2 of 48?", options: ["20", "22", "24", "26"], correct: 2 },
      { q: "8 × 7 = ?", options: ["54", "56", "58", "64"], correct: 1 },
      { q: "45 + 38 = ?", options: ["73", "83", "85", "93"], correct: 1 },
      { q: "What is 10% of 250?", options: ["20", "25", "30", "35"], correct: 1 },
      { q: "72 ÷ 8 = ?", options: ["7", "8", "9", "10"], correct: 2 },
    ],
    english: [
      { q: "What is the plural of 'child'?", options: ["childs", "children", "childes", "childrens"], correct: 1 },
      { q: "Which is a verb?", options: ["happy", "quickly", "run", "beautiful"], correct: 2 },
      { q: "What is an antonym of 'hot'?", options: ["warm", "cold", "heat", "fire"], correct: 1 },
      { q: "Which is correct?", options: ["Their going home", "There going home", "They're going home", "Theyre going home"], correct: 2 },
      { q: "What is a synonym for 'big'?", options: ["small", "large", "tiny", "thin"], correct: 1 },
      { q: "Which is a noun?", options: ["quickly", "beautiful", "happiness", "slowly"], correct: 2 },
      { q: "Past tense of 'go'?", options: ["goed", "went", "gone", "going"], correct: 1 },
      { q: "Which needs a capital?", options: ["dog", "london", "table", "happy"], correct: 1 },
      { q: "What type of word is 'slowly'?", options: ["noun", "verb", "adjective", "adverb"], correct: 3 },
      { q: "Plural of 'mouse'?", options: ["mouses", "mice", "mouse", "mousen"], correct: 1 },
      { q: "Which is an adjective?", options: ["run", "happy", "quickly", "jump"], correct: 1 },
      { q: "What is 'can't' short for?", options: ["can it", "cannot", "could not", "can do"], correct: 1 },
    ],
    science: [
      { q: "What gas do plants release?", options: ["Carbon dioxide", "Nitrogen", "Oxygen", "Hydrogen"], correct: 2 },
      { q: "How many planets in our solar system?", options: ["7", "8", "9", "10"], correct: 1 },
      { q: "What force pulls objects down?", options: ["Magnetism", "Friction", "Gravity", "Push"], correct: 2 },
      { q: "What do herbivores eat?", options: ["Meat", "Plants", "Both", "Nothing"], correct: 1 },
      { q: "What state of matter is ice?", options: ["Solid", "Liquid", "Gas", "Plasma"], correct: 0 },
      { q: "What organ pumps blood?", options: ["Brain", "Lungs", "Heart", "Liver"], correct: 2 },
      { q: "What gas do we breathe in?", options: ["Carbon dioxide", "Oxygen", "Nitrogen", "Helium"], correct: 1 },
      { q: "What is H₂O?", options: ["Salt", "Sugar", "Water", "Air"], correct: 2 },
      { q: "What do plants need to make food?", options: ["Darkness", "Sunlight", "Cold", "Salt"], correct: 1 },
      { q: "Which is a mammal?", options: ["Snake", "Fish", "Dolphin", "Frog"], correct: 2 },
      { q: "What is the largest organ?", options: ["Heart", "Brain", "Skin", "Liver"], correct: 2 },
      { q: "How many legs does a spider have?", options: ["6", "8", "10", "12"], correct: 1 },
    ]
  };

  const getRandomQuestion = useCallback(() => {
    const questions = questionBanks[subject] || questionBanks.maths;
    return questions[Math.floor(Math.random() * questions.length)];
  }, [subject]);

  const startGame = useCallback(() => {
    setGameState('playing');
    setTimeLeft(60);
    setScore(0);
    setStreak(0);
    setBestStreak(0);
    setQuestionsAnswered(0);
    setCorrectAnswers(0);
    setCurrentQuestion(getRandomQuestion());
    setSelectedAnswer(null);
    setShowResult(false);
  }, [getRandomQuestion]);

  // Timer
  useEffect(() => {
    if (gameState === 'playing' && timeLeft > 0) {
      timerRef.current = setTimeout(() => {
        setTimeLeft(t => t - 1);
      }, 1000);
      return () => clearTimeout(timerRef.current);
    } else if (timeLeft === 0 && gameState === 'playing') {
      endGame();
    }
  }, [gameState, timeLeft]);

  const endGame = async () => {
    setGameState('finished');
    clearTimeout(timerRef.current);
    
    // Calculate points
    const basePoints = correctAnswers * 10;
    const streakBonus = bestStreak * 5;
    const totalPoints = basePoints + streakBonus;
    
    try {
      await api.post('/games/complete', {
        game_id: 'quick-quiz',
        score: totalPoints,
        time_seconds: 60 - timeLeft,
        metadata: {
          questions_answered: questionsAnswered,
          correct_answers: correctAnswers,
          best_streak: bestStreak,
          subject: subject
        }
      });
      toast.success(`🎉 You earned ${totalPoints} points!`);
    } catch (error) {
      console.error('Error saving game:', error);
    }
  };

  const handleAnswer = (index) => {
    if (showResult || gameState !== 'playing') return;
    
    setSelectedAnswer(index);
    setShowResult(true);
    setQuestionsAnswered(q => q + 1);
    
    const isCorrect = index === currentQuestion.correct;
    
    if (isCorrect) {
      setCorrectAnswers(c => c + 1);
      setScore(s => s + 10 + (streak * 2)); // Bonus for streaks
      setStreak(s => {
        const newStreak = s + 1;
        if (newStreak > bestStreak) setBestStreak(newStreak);
        return newStreak;
      });
    } else {
      setStreak(0);
    }
    
    // Next question after brief delay
    setTimeout(() => {
      setCurrentQuestion(getRandomQuestion());
      setSelectedAnswer(null);
      setShowResult(false);
    }, 800);
  };

  const subjectColors = {
    maths: 'amber',
    english: 'pink',
    science: 'emerald'
  };
  const color = subjectColors[subject] || 'purple';

  return (
    <StudentLayout>
      <div data-testid="quick-quiz-game" className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button 
            onClick={() => navigate('/student/games')}
            className="flex items-center gap-2 text-slate-600 hover:text-purple-600 font-medium"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Games
          </button>
        </div>

        {/* Game Header */}
        <div className={`student-card bg-${color}-100 border-${color}-300 mb-6`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-14 h-14 bg-${color}-500 rounded-2xl border-2 border-gray-800 flex items-center justify-center`}>
                <Zap className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="font-heading text-2xl font-bold text-slate-800">Quick Quiz Rush</h1>
                <p className="text-slate-600 capitalize">{subject} - 60 seconds!</p>
              </div>
            </div>
            
            {gameState === 'playing' && (
              <div className="flex items-center gap-4">
                <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${timeLeft <= 10 ? 'bg-red-100' : 'bg-white'}`}>
                  <Clock className={`w-5 h-5 ${timeLeft <= 10 ? 'text-red-600' : 'text-slate-600'}`} />
                  <span className={`font-heading text-2xl font-bold ${timeLeft <= 10 ? 'text-red-600' : 'text-slate-800'}`}>
                    {timeLeft}s
                  </span>
                </div>
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full">
                  <Star className="w-5 h-5 text-amber-500" />
                  <span className="font-heading text-xl font-bold text-slate-800">{score}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Ready Screen */}
        {gameState === 'ready' && (
          <div className="student-card text-center py-12">
            <div className={`w-20 h-20 bg-${color}-400 rounded-full flex items-center justify-center mx-auto mb-6`}>
              <Zap className="w-12 h-12 text-white" />
            </div>
            <h2 className="font-heading text-3xl font-bold text-slate-800 mb-4">Ready?</h2>
            <p className="text-lg text-slate-600 mb-8">
              Answer as many {subject} questions as you can in 60 seconds!<br/>
              Build streaks for bonus points!
            </p>
            <Button onClick={startGame} className="student-btn text-lg px-10 py-6">
              Start Quiz!
              <Zap className="w-6 h-6 ml-2" />
            </Button>
          </div>
        )}

        {/* Playing Screen */}
        {gameState === 'playing' && currentQuestion && (
          <div className="student-card">
            {/* Streak indicator */}
            {streak > 0 && (
              <div className="text-center mb-4">
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-700 rounded-full font-bold">
                  🔥 {streak} streak!
                </span>
              </div>
            )}
            
            <h2 className="font-heading text-2xl font-bold text-slate-800 text-center mb-8">
              {currentQuestion.q}
            </h2>
            
            <div className="grid grid-cols-2 gap-4">
              {currentQuestion.options.map((option, index) => {
                let bgClass = 'bg-white hover:bg-slate-50 border-slate-300';
                if (showResult) {
                  if (index === currentQuestion.correct) {
                    bgClass = 'bg-emerald-100 border-emerald-500';
                  } else if (index === selectedAnswer && index !== currentQuestion.correct) {
                    bgClass = 'bg-red-100 border-red-500';
                  }
                } else if (index === selectedAnswer) {
                  bgClass = 'bg-purple-100 border-purple-500';
                }
                
                return (
                  <button
                    key={index}
                    onClick={() => handleAnswer(index)}
                    disabled={showResult}
                    className={`p-6 rounded-2xl border-2 font-semibold text-lg transition-all ${bgClass}`}
                    data-testid={`answer-${index}`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      {showResult && index === currentQuestion.correct && (
                        <CheckCircle className="w-6 h-6 text-emerald-600" />
                      )}
                      {showResult && index === selectedAnswer && index !== currentQuestion.correct && (
                        <XCircle className="w-6 h-6 text-red-600" />
                      )}
                      {option}
                    </div>
                  </button>
                );
              })}
            </div>
            
            <div className="mt-6 text-center text-slate-500">
              Questions: {questionsAnswered} | Correct: {correctAnswers}
            </div>
          </div>
        )}

        {/* Finished Screen */}
        {gameState === 'finished' && (
          <div className="student-card text-center py-12">
            <div className="w-20 h-20 bg-amber-400 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
              <Trophy className="w-12 h-12 text-white" />
            </div>
            <h2 className="font-heading text-3xl font-bold text-slate-800 mb-2">Time's Up!</h2>
            
            <div className="grid grid-cols-3 gap-4 my-8">
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="text-3xl font-bold text-purple-600">{questionsAnswered}</p>
                <p className="text-sm text-slate-500">Questions</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="text-3xl font-bold text-emerald-600">{correctAnswers}</p>
                <p className="text-sm text-slate-500">Correct</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="text-3xl font-bold text-amber-600">{bestStreak}</p>
                <p className="text-sm text-slate-500">Best Streak</p>
              </div>
            </div>
            
            <div className="flex items-center justify-center gap-2 mb-8">
              <Star className="w-8 h-8 text-amber-400 fill-amber-400" />
              <span className="font-heading text-4xl font-bold text-amber-600">
                +{correctAnswers * 10 + bestStreak * 5}
              </span>
              <span className="text-slate-600 text-xl">points</span>
            </div>
            
            <div className="flex gap-4 justify-center">
              <Button
                variant="outline"
                onClick={startGame}
                className="border-purple-400 text-purple-700"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Play Again
              </Button>
              <Button
                onClick={() => navigate('/student/games')}
                className="student-btn"
              >
                More Games
              </Button>
            </div>
          </div>
        )}
      </div>
    </StudentLayout>
  );
};

export default QuickQuizGame;
